import { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, Pencil, Trash2, Tag, Save, X, Loader2 } from 'lucide-react'
import { api } from '@/lib/api-client'

// ── Types ──────────────────────────────────────────────────────────────────────

type ElementType =
  | 'form_name'
  | 'submission_id'
  | 'datetime'
  | 'barcode'
  | 'qrcode'
  | 'custom_text'
  | 'operator'
  | 'department'

interface LabelElement {
  id: string
  type: ElementType
  x: number
  y: number
  w: number
  h: number
  text?: string
  font_size?: number
  bold?: boolean
  align?: 'left' | 'center' | 'right'
}

interface LabelTemplate {
  id: number
  name: string
  width_mm: number
  height_mm: number
  font_size_title: number
  font_size_body: number
  show_submission_id: boolean
  show_barcode: boolean
  show_qrcode: boolean
  show_datetime: boolean
  show_form_name: boolean
  is_active: boolean
  layout_json?: string | null
  created_at?: string
  updated_at?: string
}

// ── Constants ──────────────────────────────────────────────────────────────────

const ELEMENT_META: Record<ElementType, { label: string; defaultW: number; defaultH: number; color: string }> = {
  form_name:     { label: 'Nome do Formulario',  defaultW: 96, defaultH: 8,  color: '#2563eb' },
  submission_id: { label: 'ID da Submissao',     defaultW: 50, defaultH: 7,  color: '#7c3aed' },
  datetime:      { label: 'Data e Hora',          defaultW: 50, defaultH: 7,  color: '#0891b2' },
  barcode:       { label: 'Codigo de Barras',     defaultW: 65, defaultH: 16, color: '#374151' },
  qrcode:        { label: 'QR Code',              defaultW: 22, defaultH: 22, color: '#1f2937' },
  custom_text:   { label: 'Texto Personalizado',  defaultW: 60, defaultH: 7,  color: '#16a34a' },
  operator:      { label: 'Operador',             defaultW: 50, defaultH: 7,  color: '#dc2626' },
  department:    { label: 'Departamento',         defaultW: 50, defaultH: 7,  color: '#d97706' },
}

const DEFAULT_LAYOUT: LabelElement[] = [
  { id: '1', type: 'form_name',     x: 2,  y: 2,  w: 96, h: 8,  font_size: 14, bold: true,  align: 'center' },
  { id: '2', type: 'submission_id', x: 2,  y: 12, w: 50, h: 7,  font_size: 10, align: 'left' },
  { id: '3', type: 'datetime',      x: 54, y: 12, w: 44, h: 7,  font_size: 10, align: 'right' },
  { id: '4', type: 'barcode',       x: 2,  y: 22, w: 65, h: 16 },
  { id: '5', type: 'qrcode',        x: 70, y: 20, w: 22, h: 22 },
]

// ── Helpers ────────────────────────────────────────────────────────────────────

function parseLayout(json: string | null | undefined): LabelElement[] {
  if (!json) return [...DEFAULT_LAYOUT]
  try {
    const arr = JSON.parse(json)
    if (Array.isArray(arr) && arr.length > 0) return arr
  } catch {}
  return [...DEFAULT_LAYOUT]
}

function layoutToFlags(elements: LabelElement[]) {
  const types = new Set(elements.map(e => e.type))
  return {
    show_form_name:    types.has('form_name'),
    show_submission_id: types.has('submission_id'),
    show_datetime:     types.has('datetime'),
    show_barcode:      types.has('barcode'),
    show_qrcode:       types.has('qrcode'),
  }
}

function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
}

// ── Label card preview ──────────────────────────────────────────────────────────

function LabelCardPreview({ t }: { t: LabelTemplate }) {
  const elements = parseLayout(t.layout_json)
  const scale = Math.min(240 / t.width_mm, 100 / t.height_mm)
  const w = t.width_mm * scale
  const h = t.height_mm * scale
  return (
    <div className="flex justify-center py-2">
      <div className="relative border border-gray-200 bg-white rounded overflow-hidden" style={{ width: w, height: h }}>
        {elements.map(el => {
          const meta = ELEMENT_META[el.type]
          return (
            <div
              key={el.id}
              className="absolute rounded-sm"
              style={{
                left: el.x * scale,
                top: el.y * scale,
                width: Math.max(4, el.w * scale),
                height: Math.max(3, el.h * scale),
                backgroundColor: meta.color,
                opacity: 0.7,
              }}
            />
          )
        })}
        <div className="absolute bottom-0.5 right-0.5 text-gray-300 leading-none" style={{ fontSize: 6 }}>
          {t.width_mm}x{t.height_mm}
        </div>
      </div>
    </div>
  )
}

// ── Designer canvas ────────────────────────────────────────────────────────────

const CANVAS_W_PX = 460

function LabelDesigner({
  widthMm, heightMm, elements, onChange, selectedId, onSelectId,
}: {
  widthMm: number
  heightMm: number
  elements: LabelElement[]
  onChange: (els: LabelElement[]) => void
  selectedId: string | null
  onSelectId: (id: string | null) => void
}) {
  const scale = CANVAS_W_PX / widthMm
  const canvasH = heightMm * scale
  const canvasRef = useRef<HTMLDivElement>(null)
  const elementsRef = useRef(elements)
  elementsRef.current = elements

  const draggingRef = useRef<{
    id: string; startX: number; startY: number; origX: number; origY: number
  } | null>(null)

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const d = draggingRef.current
      if (!d) return
      const els = elementsRef.current
      const el = els.find(x => x.id === d.id)
      if (!el) return
      const dx = (e.clientX - d.startX) / scale
      const dy = (e.clientY - d.startY) / scale
      const newX = Math.max(0, Math.min(widthMm - el.w, parseFloat((d.origX + dx).toFixed(1))))
      const newY = Math.max(0, Math.min(heightMm - el.h, parseFloat((d.origY + dy).toFixed(1))))
      onChange(els.map(x => x.id === d.id ? { ...x, x: newX, y: newY } : x))
    }
    const onMouseUp = () => { draggingRef.current = null }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [scale, widthMm, heightMm, onChange])

  const handleMouseDown = (e: React.MouseEvent, el: LabelElement) => {
    e.preventDefault()
    e.stopPropagation()
    onSelectId(el.id)
    draggingRef.current = { id: el.id, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y }
  }

  return (
    <div
      ref={canvasRef}
      className="relative border-2 border-gray-400 bg-white rounded select-none shadow-md"
      style={{ width: CANVAS_W_PX, height: Math.max(60, canvasH) }}
      onClick={e => { if (e.target === canvasRef.current) onSelectId(null) }}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-25"
        style={{
          backgroundImage: 'radial-gradient(circle, #9ca3af 1px, transparent 1px)',
          backgroundSize: `${scale * 5}px ${scale * 5}px`,
        }}
      />
      {elements.map(el => {
        const meta = ELEMENT_META[el.type]
        const isSelected = el.id === selectedId
        const elW = Math.max(4, el.w * scale)
        const elH = Math.max(4, el.h * scale)
        const fontSize = Math.max(8, Math.min(12, elH * 0.38))
        return (
          <div
            key={el.id}
            onMouseDown={e => handleMouseDown(e, el)}
            className={`absolute flex items-center justify-center overflow-hidden rounded cursor-move text-white font-medium
              ${isSelected ? 'ring-2 ring-offset-1 ring-yellow-400 z-10' : 'z-0 hover:opacity-95'}`}
            style={{
              left: el.x * scale,
              top: el.y * scale,
              width: elW,
              height: elH,
              backgroundColor: meta.color,
              opacity: isSelected ? 1 : 0.82,
              fontSize,
            }}
            title={meta.label}
          >
            <span className="truncate px-1">
              {el.type === 'custom_text' ? (el.text || 'Texto...')
                : el.type === 'barcode' ? 'Barcode'
                : el.type === 'qrcode' ? 'QR'
                : meta.label}
            </span>
          </div>
        )
      })}
      <div className="absolute bottom-1 right-1 text-gray-300 text-xs pointer-events-none select-none">
        {widthMm}x{heightMm} mm
      </div>
    </div>
  )
}

// ── Element palette ────────────────────────────────────────────────────────────

function ElementPalette({ onAdd }: { onAdd: (type: ElementType) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      {(Object.keys(ELEMENT_META) as ElementType[]).map(type => {
        const meta = ELEMENT_META[type]
        return (
          <button
            key={type}
            type="button"
            onClick={() => onAdd(type)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white font-medium hover:opacity-85 transition-opacity text-left w-full"
            style={{ backgroundColor: meta.color }}
          >
            <Plus className="h-3 w-3 shrink-0" />
            <span className="truncate">{meta.label}</span>
          </button>
        )
      })}
    </div>
  )
}

// ── Element properties ─────────────────────────────────────────────────────────

function ElementProperties({
  element, onChange, onDelete,
}: {
  element: LabelElement
  onChange: (el: LabelElement) => void
  onDelete: () => void
}) {
  const f = (k: keyof LabelElement) => (v: any) => onChange({ ...element, [k]: v })
  const isText = !['barcode', 'qrcode'].includes(element.type)
  const meta = ELEMENT_META[element.type]

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs text-white font-medium truncate" style={{ backgroundColor: meta.color }}>
          {meta.label}
        </span>
        <button type="button" onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 shrink-0" title="Remover">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Posicao (mm)</p>
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">X</label>
            <input type="number" step="0.5" min="0" className="input w-full py-1 text-sm" value={element.x} onChange={e => f('x')(parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Y</label>
            <input type="number" step="0.5" min="0" className="input w-full py-1 text-sm" value={element.y} onChange={e => f('y')(parseFloat(e.target.value) || 0)} />
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Dimensoes (mm)</p>
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Largura</label>
            <input type="number" step="0.5" min="2" className="input w-full py-1 text-sm" value={element.w} onChange={e => f('w')(parseFloat(e.target.value) || 2)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Altura</label>
            <input type="number" step="0.5" min="2" className="input w-full py-1 text-sm" value={element.h} onChange={e => f('h')(parseFloat(e.target.value) || 2)} />
          </div>
        </div>
      </div>

      {isText && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Texto</p>
          <div className="grid grid-cols-2 gap-1.5 mb-1.5">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Fonte (pt)</label>
              <input type="number" min="6" max="36" className="input w-full py-1 text-sm" value={element.font_size ?? 10} onChange={e => f('font_size')(parseInt(e.target.value) || 10)} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Alinhamento</label>
              <select className="input w-full py-1 text-sm" value={element.align ?? 'left'} onChange={e => f('align')(e.target.value)}>
                <option value="left">Esq.</option>
                <option value="center">Centro</option>
                <option value="right">Dir.</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={element.bold ?? false} onChange={e => f('bold')(e.target.checked)} className="rounded" />
            <span className="text-sm text-gray-700">Negrito</span>
          </label>
        </div>
      )}

      {element.type === 'custom_text' && (
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Conteudo</label>
          <input className="input w-full text-sm" value={element.text ?? ''} onChange={e => f('text')(e.target.value)} placeholder="Texto fixo a mostrar" />
        </div>
      )}
    </div>
  )
}

// ── Template modal ─────────────────────────────────────────────────────────────

function TemplateModal({
  initial, onClose, onSaved,
}: {
  initial?: LabelTemplate
  onClose: () => void
  onSaved: (t: LabelTemplate) => void
}) {
  const isEdit = Boolean(initial?.id)
  const [name, setName] = useState(initial?.name ?? '')
  const [widthMm, setWidthMm] = useState(initial?.width_mm ?? 100)
  const [heightMm, setHeightMm] = useState(initial?.height_mm ?? 50)
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)
  const [elements, setElements] = useState<LabelElement[]>(() => parseLayout(initial?.layout_json))
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedElement = elements.find(e => e.id === selectedId) ?? null

  const addElement = (type: ElementType) => {
    const meta = ELEMENT_META[type]
    const el: LabelElement = {
      id: newId(),
      type,
      x: 2,
      y: 2,
      w: meta.defaultW,
      h: meta.defaultH,
      ...(!['barcode', 'qrcode'].includes(type) && { font_size: 10, align: 'left' as const }),
    }
    setElements(prev => [...prev, el])
    setSelectedId(el.id)
  }

  const updateElement = (updated: LabelElement) =>
    setElements(prev => prev.map(e => e.id === updated.id ? updated : e))

  const deleteElement = (id: string) => {
    setElements(prev => prev.filter(e => e.id !== id))
    setSelectedId(null)
  }

  async function handleSave() {
    if (!name.trim()) { setError('O nome e obrigatorio'); return }
    setSaving(true)
    setError(null)
    try {
      const flags = layoutToFlags(elements)
      const payload = {
        name: name.trim(),
        width_mm: widthMm,
        height_mm: heightMm,
        font_size_title: 14,
        font_size_body: 10,
        ...flags,
        is_active: isActive,
        layout_json: JSON.stringify(elements),
      }
      const url = isEdit ? `/api/settings/labels/${initial!.id}` : '/api/settings/labels'
      const method = isEdit ? 'put' : 'post'
      const res = await api[method]<LabelTemplate>(url, payload)
      onSaved(res.data)
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Erro ao guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ maxHeight: 'calc(100vh - 24px)' }}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b shrink-0">
          <h2 className="text-base font-semibold">{isEdit ? 'Editar Etiqueta' : 'Nova Etiqueta'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        {error && (
          <p className="mx-5 mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 shrink-0">{error}</p>
        )}

        <div className="px-5 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-end shrink-0">
          <div className="flex-1 min-w-44">
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome</label>
            <input className="input w-full" value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Etiqueta 100x50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Largura (mm)</label>
            <input type="number" min={30} max={300} className="input w-24" value={widthMm}
              onChange={e => setWidthMm(Number(e.target.value))}
              onBlur={e => setWidthMm(Math.min(300, Math.max(30, Number(e.target.value) || 30)))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Altura (mm)</label>
            <input type="number" min={20} max={400} className="input w-24" value={heightMm}
              onChange={e => setHeightMm(Number(e.target.value))}
              onBlur={e => setHeightMm(Math.min(400, Math.max(20, Number(e.target.value) || 20)))} />
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer pb-0.5">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="rounded" />
            <span className="text-sm font-medium text-gray-700">Ativo</span>
          </label>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-48 border-r p-3 overflow-y-auto shrink-0 bg-gray-50">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Campos</p>
            <ElementPalette onAdd={addElement} />
            <p className="text-xs text-gray-400 mt-3 leading-snug">Clique para adicionar.<br />Arraste para posicionar.</p>
          </div>

          <div className="flex-1 overflow-auto p-4 flex flex-col items-center bg-gray-100 gap-2">
            <p className="text-xs text-gray-400 self-start">Arraste os campos para os posicionar na etiqueta</p>
            <LabelDesigner
              widthMm={widthMm}
              heightMm={heightMm}
              elements={elements}
              onChange={setElements}
              selectedId={selectedId}
              onSelectId={setSelectedId}
            />
          </div>

          <div className="w-52 border-l p-3 overflow-y-auto shrink-0">
            {selectedElement ? (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Propriedades</p>
                <ElementProperties
                  element={selectedElement}
                  onChange={updateElement}
                  onDelete={() => deleteElement(selectedElement.id)}
                />
              </>
            ) : (
              <div className="text-xs text-gray-400 mt-8 text-center leading-relaxed">
                Clique num campo para editar as suas propriedades
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t bg-gray-50 shrink-0">
          <span className="text-xs text-gray-400 mr-auto">{elements.length} campo(s)</span>
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function LabelsSettingsPage() {
  const [templates, setTemplates] = useState<LabelTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'new' | LabelTemplate | null>(null)
  const [deleting, setDeleting] = useState<number | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await api.get<LabelTemplate[]>('/api/settings/labels')
      setTemplates(res.data)
    } catch {
      setError('Erro ao carregar templates de etiqueta')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleDelete(id: number) {
    if (!confirm('Eliminar este template de etiqueta?')) return
    setDeleting(id)
    try {
      await api.delete(`/api/settings/labels/${id}`)
      setTemplates(prev => prev.filter(t => t.id !== id))
    } catch {
      alert('Erro ao eliminar')
    } finally {
      setDeleting(null)
    }
  }

  function handleSaved(t: LabelTemplate) {
    setTemplates(prev => {
      const idx = prev.findIndex(x => x.id === t.id)
      if (idx >= 0) { const arr = [...prev]; arr[idx] = t; return arr }
      return [...prev, t]
    })
    setModal(null)
  }

  function getChips(t: LabelTemplate) {
    try {
      if (t.layout_json) {
        const els: LabelElement[] = JSON.parse(t.layout_json)
        const types = [...new Set(els.map(e => e.type))]
        return types.map(type => ({ key: type, label: ELEMENT_META[type]?.label ?? type, color: ELEMENT_META[type]?.color ?? '#6b7280' }))
      }
    } catch {}
    const chips: { key: string; label: string; color: string }[] = []
    if (t.show_form_name)    chips.push({ key: 'form_name',     label: 'Formulario', color: ELEMENT_META.form_name.color })
    if (t.show_submission_id) chips.push({ key: 'submission_id', label: 'ID',         color: ELEMENT_META.submission_id.color })
    if (t.show_datetime)     chips.push({ key: 'datetime',      label: 'Data',       color: ELEMENT_META.datetime.color })
    if (t.show_barcode)      chips.push({ key: 'barcode',       label: 'Barcode',    color: ELEMENT_META.barcode.color })
    if (t.show_qrcode)       chips.push({ key: 'qrcode',        label: 'QR',         color: ELEMENT_META.qrcode.color })
    return chips
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Tag className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Etiquetas</h1>
            <p className="text-sm text-gray-500">Configure templates de etiquetas para impressao em workflows</p>
          </div>
        </div>
        <button onClick={() => setModal('new')} className="btn btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nova Etiqueta
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
        <strong>Como funciona:</strong> Cria templates aqui — arrasta os campos para os posicionar livremente na etiqueta.
        Em <strong>Workflows &rarr; Transicoes</strong> seleciona qual template usar. Ao aprovar uma tarefa, o utilizador pode imprimir a etiqueta da submissao.
      </div>

      {loading && <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>}
      {error && !loading && <div className="text-center py-12 text-red-600">{error}</div>}

      {!loading && !error && templates.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
          <Tag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Nenhum template configurado</p>
          <p className="text-gray-400 text-sm mb-4">Cria o primeiro template de etiqueta</p>
          <button onClick={() => setModal('new')} className="btn btn-primary"><Plus className="h-4 w-4 mr-1" />Nova Etiqueta</button>
        </div>
      )}

      {!loading && !error && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <div key={t.id} className={`bg-white border rounded-xl shadow-sm hover:shadow-md transition-shadow ${!t.is_active ? 'opacity-60' : ''}`}>
              <div className="px-4 pt-4 pb-2">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{t.name}</h3>
                    <p className="text-xs text-gray-500">{t.width_mm} x {t.height_mm} mm</p>
                  </div>
                  <span className={`ml-2 shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {t.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
              <LabelCardPreview t={t} />
              <div className="px-4 pb-3 flex flex-wrap gap-1">
                {getChips(t).map(chip => (
                  <span key={chip.key} className="text-xs text-white px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: chip.color }}>
                    {chip.label}
                  </span>
                ))}
              </div>
              <div className="flex border-t divide-x">
                <button onClick={() => setModal(t)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  <Pencil className="h-3.5 w-3.5" />Editar
                </button>
                <button onClick={() => handleDelete(t.id)} disabled={deleting === t.id} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50">
                  {deleting === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <TemplateModal
          initial={modal === 'new' ? undefined : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}